import { cancelSelectEvent, changeSymbolForWidget, getCSSStyles, getEChartsTooltipTheme, getHeaderPinButtonHTML, getHeaderPinStateClass, getNoDataHTML, getWidgetDataURI, normalizeWidgetTheme, setupHeaderPin, toggleWidgetTheme } from './../../helpers';
import { FinancialConfig, WidgetEvent, WidgetEventHandler, WidgetEventType } from './../../types';

interface TransactionTypeItem {
    name: string;
    value: number;
    color: string;
}

const PURCHASE_COLOR = '#3DFC7B';
const SALE_COLOR = '#FFC01E';
const PARTIAL_SALE_COLOR = '#FF4A4A';

export class TraderverseCongressTradingActivityByTransactionType {
    private container: HTMLElement;
    private config: FinancialConfig;
    private eventHandlers: Map<WidgetEventType, WidgetEventHandler[]> = new Map();
    private updateInterval: number | null = null;
    private shadowRoot: ShadowRoot;
    private currentData: TransactionTypeItem[] = [];
    private currentChart: any = null;
    private widgetResizeObserver: ResizeObserver | null = null;
    private widgetIndex: number = 0;
    private hiddenTypes: Set<string> = new Set();

    constructor(config: FinancialConfig) {
        this.config = { ...this.getDefaultConfig(), ...config };
        this.config.theme = normalizeWidgetTheme(this.config.theme);
        this.container = this.resolveContainer(config.container);

        if ((this.container as any).shadowRoot) {
            this.shadowRoot = (this.container as any).shadowRoot;
        } else {
            this.shadowRoot = this.container.attachShadow({ mode: 'closed' });
        }
        this.init();

        this.widgetResizeObserver = new ResizeObserver(() => {
            this.updateDisplay();
        });
        setTimeout(() => {
            const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
            if (widget) this.widgetResizeObserver?.observe(widget);
        }, 0);
    }

    private getDefaultConfig(): Partial<FinancialConfig> {
        return {
            headerPinned: true,
            type: 'Congress Trading Activity',
            theme: 'default-dark',
            width: '100%',
            height: '400px',
            autosize: true
        } as any;
    }

    private resolveContainer(container: string | HTMLElement): HTMLElement {
        if (typeof container === 'string') {
            const el = document.getElementById(container) || document.querySelector(container);
            if (!el) throw new Error(`Container not found: ${container}`);
            return el as HTMLElement;
        }
        return container;
    }

    /** Sample data used until the transaction-type API is available. */
    private getDummyData(): TransactionTypeItem[] {
        return [
            { name: 'Purchase (House)', value: 285, color: PURCHASE_COLOR },
            { name: 'Purchase (Senate)', value: 525, color: PURCHASE_COLOR },
            { name: 'Sale (House)', value: 335, color: SALE_COLOR },
            { name: 'Sale (Senate)', value: 428, color: SALE_COLOR },
            { name: 'Sale (Partial) (House)', value: 378, color: PARTIAL_SALE_COLOR },
            { name: 'Sale (Partial) (Senate)', value: 468, color: PARTIAL_SALE_COLOR }
        ];
    }

    private async init(): Promise<void> {
        this.attachStyles();
        this.createWidget();
        const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        if (widget) widget.classList.add('loading');
        await this.injectChartLibrary();
        await this.setupChart();
        if (widget) widget.classList.remove('loading');
        this.emit('ready', {});
        cancelSelectEvent(this.shadowRoot);
        this.config.onReady?.();
    }

    private createWidget(): void {
        let themeClass = 'defaultDarkMode';
        let dataTheme = 'default-dark';

        if (this.config.theme === 'white') {
            themeClass = 'whiteMode';
            dataTheme = 'white';
        } else if (this.config.theme === 'black') {
            themeClass = 'blackMode';
            dataTheme = 'black';
        } else if (this.config.theme === 'default-lite') {
            themeClass = 'defaultLightMode';
            dataTheme = 'default-lite';
        }
        const widgetHtml = `
        <div style="width: ${typeof this.config.width == "number" ? this.config.width + 'px' : this.config.width};height: ${typeof this.config.height == "number" ? this.config.height + 'px' : this.config.height};" class="widgetBox ${themeClass} loading ${getHeaderPinStateClass(this.config)}" data-testing-id="congress-trading-activity-by-transaction-type-widget-${this.widgetIndex}" data-theme="${dataTheme}">
          <div class="headTop">
            <h4 class="title market">${this.getTitleIconSvg()} Congress Trading Activity by Transaction Type</h4>
            <div class="actionCol">${getHeaderPinButtonHTML(this.config)}
                <i class="pi pi-ellipsis-v"></i>
            </div>
          </div>
          <div class="inBody">
            <div class="optionsRow">
                <div class="item">
                    <div class="legendsCollection" id="transaction-type-legends"></div>
                </div>
                <span></span>
            </div>
            <div class="chartDv" id="congressTransactionTypes">
                <div id="chartContainer" class="chart" data-testing-id="congress-trading-activity-by-transaction-type-chart"></div>
            </div>
          </div>
        </div>
    `;
        this.shadowRoot.innerHTML = widgetHtml;
        setupHeaderPin(this.shadowRoot, this.config);
    }

    private async attachStyles(): Promise<void> {
        try {
            const styleEl = await getCSSStyles();
            this.shadowRoot.appendChild(styleEl);
        } catch (err) {
            console.error('Failed to load CSS file:', err);
        }
    }

    private async injectChartLibrary(): Promise<boolean> {
        return new Promise<boolean>((resolve) => {
            if ((window as any).echarts) return resolve(true);
            if (this.shadowRoot.querySelector('script[data-chart-lib]')) {
                const script = this.shadowRoot.querySelector('script[data-chart-lib]') as HTMLScriptElement;
                script.onload = () => resolve(true);
            } else {
                const script = document.createElement('script');
                script.src = 'https://cdn.jsdelivr.net/npm/echarts@5/dist/echarts.min.js';
                script.async = true;
                script.setAttribute('data-chart-lib', 'true');
                this.shadowRoot.appendChild(script);
                script.onload = () => resolve(true);
            }
        });
    }

    private async setupChart(_symbol?: string): Promise<void> {
        const widgetBox = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        try {
            if (widgetBox) widgetBox.classList.add('loading');
            this.currentData = this.getDummyData();
            this.hiddenTypes.clear();
            this.updateDisplay();
            this.emit('dataUpdate', this.currentData);
        } finally {
            widgetBox?.classList.remove('loading');
        }
    }

    private updateDisplay(): void {
        if (!this.currentData.length || !(window as any).echarts) {
            setTimeout(() => this.updateDisplay(), 100);
            return;
        }
        this.drawChart();
        this.renderLegends();
    }

    private drawChart(): void {
        const echarts = (window as any).echarts;
        const container = this.shadowRoot.querySelector('#chartContainer') as HTMLDivElement;
        const widgetBox = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        if (!container) return;

        const instance = echarts.getInstanceByDom(container);
        if (instance) echarts.dispose(instance);

        const visible = this.currentData.filter(item => !this.hiddenTypes.has(item.name));
        if (!visible.length) {
            container.innerHTML = getNoDataHTML();
            return;
        }
        container.innerHTML = '';
        container.style.width = '100%';
        container.style.height = '100%';
        container.style.minHeight = '260px';

        const textColor = getComputedStyle(widgetBox).getPropertyValue('--charts-text-color');
        const axisLineColor = getComputedStyle(widgetBox).getPropertyValue('--charts-axis-color');
        const splitLineColor = getComputedStyle(widgetBox).getPropertyValue('--charts-split-line-color');
        const fontSize = getComputedStyle(widgetBox).getPropertyValue('--font-size-sm');

        const option = {
            backgroundColor: 'transparent',
            tooltip: {
                ...getEChartsTooltipTheme(widgetBox),
                trigger: 'item',
                axisPointer: { type: 'shadow' },
                formatter: (params: any) => {
                    const item = visible[params.dataIndex];
                    return `<div style="display:flex;align-items:center;gap:8px">
                                <span style="display:inline-block;width:9px;height:9px;border-radius:50%;background:${item.color}"></span>
                                <span style="font-weight:600">${item.name}</span>
                                <span style="font-weight:600">${item.value.toLocaleString()}</span>
                            </div>`;
                }
            },
            grid: { left: 10, right: 12, top: 24, bottom: 8, containLabel: true },
            xAxis: {
                type: 'category',
                data: visible.map(item => item.name),
                axisLabel: { show: false },
                axisTick: { show: false },
                axisLine: {
                    show: true,
                    lineStyle: { color: axisLineColor }
                },
                splitLine: { show: false },
                z: 10
            },
            yAxis: {
                type: 'value',
                axisLabel: {
                    color: textColor,
                    fontSize: fontSize,
                    formatter: (value: number) => value.toLocaleString()
                },
                axisTick: { show: false },
                axisLine: { show: false },
                splitLine: {
                    show: true,
                    lineStyle: { color: splitLineColor, type: 'dashed' }
                }
            },
            series: [
                {
                    name: 'Transactions',
                    type: 'bar',
                    data: visible.map(item => ({
                        value: item.value,
                        itemStyle: { color: item.color, borderRadius: [4, 4, 0, 0] }
                    })),
                    barWidth: '55%',
                    barCategoryGap: '35%',
                    label: {
                        show: true,
                        position: 'insideBottom',
                        rotate: 90,
                        align: 'left',
                        verticalAlign: 'middle',
                        distance: 12,
                        color: '#FFFFFF',
                        fontSize: 11,
                        fontWeight: 500,
                        formatter: (params: any) => visible[params.dataIndex].name
                    },
                    emphasis: { disabled: true }
                }
            ]
        };

        this.currentChart = echarts.init(container);
        this.currentChart.setOption(option);
        requestAnimationFrame(() => this.currentChart?.resize());
    }

    private renderLegends(): void {
        const container = this.shadowRoot.querySelector('#transaction-type-legends') as HTMLElement;
        if (!container) return;

        container.innerHTML = '';
        this.currentData.forEach(item => {
            const el = document.createElement('span');
            const isActive = !this.hiddenTypes.has(item.name);
            el.className = `toggle-series${isActive ? ' active' : ''}`;
            el.style.backgroundColor = item.color;
            el.setAttribute('data-tooltip', item.name);
            el.title = item.name;

            el.addEventListener('click', () => {
                const hide = !this.hiddenTypes.has(item.name);
                if (hide) {
                    this.hiddenTypes.add(item.name);
                } else {
                    this.hiddenTypes.delete(item.name);
                }
                el.classList.toggle('active', !hide);
                this.drawChart();
            });

            container.appendChild(el);
        });
    }

    private changeSymbol(stockId: string, stock: any = null): void {
        changeSymbolForWidget(this.shadowRoot, this.config, stock, stockId, (stockId: any) => this.setupChart(stockId), (type: any, data: any) => this.emit(type, data));
    }

    public on(type: WidgetEventType, handler: WidgetEventHandler): void {
        if (!this.eventHandlers.has(type)) this.eventHandlers.set(type, []);
        this.eventHandlers.get(type)!.push(handler);
    }
    public off(type: WidgetEventType, handler: WidgetEventHandler): void {
        const list = this.eventHandlers.get(type);
        if (list) this.eventHandlers.set(type, list.filter(h => h !== handler));
    }
    public setSymbol(symbol: string): void {
        this.changeSymbol(symbol);
        const dropdown = this.shadowRoot.querySelector('.symbol-dropdown') as HTMLSelectElement;
        if (dropdown) dropdown.value = symbol;
    }
    public async getWidgetImage(): Promise<string> {
        const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        return await getWidgetDataURI(widget);
    }

    public setTheme(theme: 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite'): void {
        if (!this.shadowRoot) return;

        this.config.theme = toggleWidgetTheme(
            this.shadowRoot,
            theme
        ) as 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite';

        this.updateDisplay();
    }
    public destroy(): void {
        if (this.updateInterval) { clearInterval(this.updateInterval); this.updateInterval = null; }
        if (this.widgetResizeObserver) { this.widgetResizeObserver.disconnect(); this.widgetResizeObserver = null; }
        if (this.currentChart) { this.currentChart.dispose(); this.currentChart = null; }
        this.eventHandlers.clear(); this.shadowRoot.innerHTML = '';
    }
    private emit(type: WidgetEventType, data: any): void {
        const handlers = this.eventHandlers.get(type);
        if (handlers) { const evt: WidgetEvent = { type, data }; handlers.forEach(h => h(evt)); }
    }
    private getTitleIconSvg(): string {
        return `<svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M18.75 28.4375H11.25C4.4625 28.4375 1.5625 25.5375 1.5625 18.75V11.25C1.5625 4.4625 4.4625 1.5625 11.25 1.5625H18.75C25.5375 1.5625 28.4375 4.4625 28.4375 11.25V18.75C28.4375 25.5375 25.5375 28.4375 18.75 28.4375ZM11.25 3.4375C5.4875 3.4375 3.4375 5.4875 3.4375 11.25V18.75C3.4375 24.5125 5.4875 26.5625 11.25 26.5625H18.75C24.5125 26.5625 26.5625 24.5125 26.5625 18.75V11.25C26.5625 5.4875 24.5125 3.4375 18.75 3.4375H11.25Z" fill="currentColor"/> <path d="M9.16252 19.05C8.96252 19.05 8.76252 18.9875 8.58752 18.85C8.17502 18.5375 8.10002 17.95 8.41252 17.5375L11.3875 13.675C11.75 13.2125 12.2625 12.9125 12.85 12.8375C13.425 12.7625 14.0125 12.925 14.475 13.2875L16.7625 15.0875C16.85 15.1625 16.9375 15.1625 17 15.15C17.05 15.15 17.1375 15.125 17.2125 15.025L20.1 11.3C20.4125 10.8875 21.0125 10.8125 21.4125 11.1375C21.825 11.45 21.9 12.0375 21.575 12.45L18.6875 16.175C18.325 16.6375 17.8125 16.9375 17.225 17C16.6375 17.075 16.0625 16.9125 15.6 16.55L13.3125 14.75C13.225 14.675 13.125 14.675 13.075 14.6875C13.025 14.6875 12.9375 14.7125 12.8625 14.8125L9.88752 18.675C9.72502 18.925 9.45002 19.05 9.16252 19.05Z" fill="currentColor"/> </svg>`;
    }

}

declare global { interface Window { TraderverseCongressTradingActivityByTransactionType: typeof TraderverseCongressTradingActivityByTransactionType; } }
if (typeof window !== 'undefined') { window.TraderverseCongressTradingActivityByTransactionType = TraderverseCongressTradingActivityByTransactionType; }
